import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Settings, Maximize, Play as PlayIcon } from "lucide-react";
import LangToggle from "../components/LangToggle";
import { useLang } from "../lib/i18n";
import { computeDirective, ARRIVAL_DURATION } from "../lib/directorFlow";
import {
  ArrivalAnnouncement,
  ArrivalBurst,
  AnticipationScene,
  CakeCelebrationScene,
  CakePrepScene,
  DinnerMessagesScene,
  FinalFarewellScene,
  PostEventScene,
  PreEventScene,
} from "../components/DirectorScenes";
import DirectorYtPlayer from "../components/DirectorYtPlayer";
import Gallery from "./Gallery";
import Memories from "./Memories";
import DanceFloor from "./DanceFloor";
import {
  fileUrl,
  getEventConfig,
  getEventState,
  getGallery,
  getKingdomInfo,
  listMusic,
  listPhotos,
  setEventState,
} from "../lib/api";

const TE = { fontFamily: "'Tiro Telugu','Noto Serif Telugu','Yatra One','Eczar',serif" };

// Modes that were previously rendered via iframe (`/gallery?embed=1`,
// `/memories?embed=1`, `/dance?embed=1`). On self-hosted static servers
// without SPA fallback those routes 404 → blank screen. We now render the
// same React components directly so no route-lookup is involved.
const INLINE_MODES = new Set(["sky", "memories", "dancing"]);

export default function Director() {
  const lang = useLang();
  const [config, setConfig] = useState(null);
  const [state, setState] = useState({ phase: "auto", phase_started_at: null });
  const [guests, setGuests] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [info, setInfo] = useState({ princess_name: "Anagha" });
  const [arrival, setArrival] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [trackIdx, setTrackIdx] = useState(0);
  // First user gesture toggles autoplay + fullscreen. Persisted per tab so
  // refreshes within the projector session don't re-prompt.
  const [showStarted, setShowStarted] = useState(() => {
    try { return sessionStorage.getItem("director_show_started") === "1"; } catch { return false; }
  });
  const audioRef = useRef(null);
  const knownIds = useRef(new Set());

  // Initial data + 5s polling. Event state polled at 2s for snappy admin control.
  useEffect(() => {
    const loadHeavy = async () => {
      try {
        const [cfg, g, k, ph, m] = await Promise.all([
          getEventConfig(),
          getGallery(),
          getKingdomInfo(),
          listPhotos(),
          listMusic(),
        ]);
        setConfig(cfg);
        setInfo(k);
        setPhotos(ph || []);
        // Director-flagged tracks for the sky/memories/cake/dinner/farewell scenes.
        const directorTracks = (m || []).filter((t) => t.for_director);
        setTracks(directorTracks);
        // Arrival detection
        const newOnes = (g || []).filter((x) => !knownIds.current.has(x.id));
        if (knownIds.current.size > 0 && newOnes.length) {
          const incoming = newOnes[0];
          setArrival(incoming);
          setTimeout(() => setArrival(null), ARRIVAL_DURATION * 1000);
        }
        (g || []).forEach((x) => knownIds.current.add(x.id));
        setGuests(g || []);
      } catch {/* ignore */}
    };
    const loadState = async () => {
      try {
        const s = await getEventState();
        setState(s);
      } catch {/* ignore */}
    };
    loadHeavy();
    loadState();
    const tmH = setInterval(loadHeavy, 5000);
    const tmS = setInterval(loadState, 2000);
    return () => { clearInterval(tmH); clearInterval(tmS); };
  }, []);

  // Tick for clock-driven directives
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const tm = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(tm);
  }, []);

  const directive = useMemo(
    () => computeDirective({ now: Date.now(), config, state }),
    [config, state, tick]
  );

  // If the directive says to auto-advance (cake_prep → cake_celebrate → dancing),
  // push the new phase to the backend so all viewers stay in sync.
  useEffect(() => {
    if (directive.autoAdvanceFrom === "cake_prep" && state.phase === "cake_prep") {
      setEventState("cake_celebrate").then(setState).catch(() => {});
    } else if (directive.autoAdvanceFrom === "cake_celebrate" && state.phase === "cake_celebrate") {
      setEventState("dancing").then(setState).catch(() => {});
    }
  }, [directive, state.phase]);

  const mode = directive.mode;
  const isInline = INLINE_MODES.has(mode);

  // Modes that should play Director's own music. Dancing is excluded because
  // the embedded /dance iframe plays its own dance-floor track.
  const audioModes = ["sky", "memories", "cake_prep", "cake_celebrate", "dinner", "farewell"];
  const audioActive = showStarted && audioModes.includes(mode) && tracks.length > 0;
  const currentTrack = tracks[trackIdx % Math.max(tracks.length, 1)];

  // Play / pause based on mode + first-gesture flag
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    if (audioActive) {
      a.play().catch(() => {/* will retry on next gesture */});
    } else {
      a.pause();
    }
  }, [audioActive, currentTrack?.id]);

  // Advance to next track when current finishes
  const onAudioEnded = () => {
    setTrackIdx((i) => (i + 1) % Math.max(tracks.length, 1));
  };

  const handleStart = async () => {
    try { sessionStorage.setItem("director_show_started", "1"); } catch {/* */}
    setShowStarted(true);
    // Try to fullscreen on this same gesture
    try {
      if (document.documentElement.requestFullscreen) {
        await document.documentElement.requestFullscreen();
      }
    } catch {/* user can press F11 if blocked */}
    // Audio will auto-start via the useEffect above
  };

  const goFullscreen = async () => {
    try { await document.documentElement.requestFullscreen(); } catch {/* */}
  };

  return (
    <div className="fixed inset-0 overflow-hidden bg-black text-blush" data-testid="director-root">
      {/* Hidden audio element — single track loop, advances via onEnded */}
      {currentTrack && (
        <audio
          ref={audioRef}
          src={fileUrl(currentTrack.path)}
          onEnded={onAudioEnded}
          // intentionally NOT autoPlay — only plays after user gesture
          preload="auto"
          data-testid="director-audio"
        />
      )}

      {/* First-gesture overlay: enables autoplay + fullscreen.
          Once dismissed, persists for the session (sessionStorage). */}
      {!showStarted && (
        <button
          onClick={handleStart}
          className="absolute inset-0 z-[120] flex flex-col items-center justify-center gap-4 text-blush"
          style={{
            background:
              "radial-gradient(ellipse at center, rgba(74,22,77,0.95), rgba(2,2,26,0.98))",
          }}
          data-testid="director-start-overlay"
        >
          <PlayIcon size={64} className="text-[#FCF6BA] drop-shadow-2xl" />
          <p className="gold-text-soft font-script drop-shadow-2xl" style={{ ...TE, fontSize: "clamp(32px, 6vw, 64px)" }}>
            {lang === "te" ? "ప్రారంభించడానికి తాకండి" : "Tap to Begin the Show"}
          </p>
          <p className="font-display text-blush/80 max-w-xl text-center px-6" style={{ ...TE, fontSize: "clamp(13px, 1.8vw, 18px)" }}>
            {lang === "te"
              ? "మొత్తం స్క్రీన్ తెరుచుకుని, సంగీతం ప్రారంభమవుతుంది"
              : "Enters fullscreen + starts music. Press Esc anytime to exit fullscreen."}
          </p>
        </button>
      )}
      {isInline && (
        <div className="absolute inset-0 w-full h-full" data-testid={`director-inline-${mode}`}>
          {mode === "sky"      && <Gallery embed />}
          {mode === "memories" && <Memories embed />}
          {mode === "dancing"  && <DanceFloor embed />}
        </div>
      )}

      {/* React scenes */}
      {mode === "pre" && <PreEventScene lang={lang} startsAt={config?.start} />}
      {mode === "doors_open" && <PreEventScene lang={lang} startsAt={config?.start} forceOpen />}
      {mode === "anticipation" && <AnticipationScene lang={lang} />}
      {mode === "cake_prep" && <CakePrepScene lang={lang} />}
      {mode === "cake_celebrate" && <CakeCelebrationScene lang={lang} guests={guests} />}
      {mode === "dinner" && <DinnerMessagesScene lang={lang} info={info} guests={guests} />}
      {mode === "farewell" && <FinalFarewellScene lang={lang} guests={guests} info={info} />}
      {mode === "post" && <PostEventScene lang={lang} />}

      {/* Arrival announcement — 45s overlay */}
      {arrival && <ArrivalAnnouncement lang={lang} guest={arrival} />}
      {/* Fireworks + balloons burst on new guest arrival (visible on sky/anticipation) */}
      {arrival && (mode === "sky" || mode === "anticipation") && <ArrivalBurst guest={arrival} />}

      {/* Invisible YouTube background player — plays per-scene tracks */}
      {showStarted && <DirectorYtPlayer scene={mode} />}

      {/* Language toggle — always visible top-right */}
      <div className="absolute top-3 right-16 z-[95]">
        <LangToggle light />
      </div>

      {/* Fullscreen toggle — also always visible (small icon button) */}
      <button
        onClick={goFullscreen}
        className="absolute top-3 right-44 z-[95] glass rounded-full p-1.5 text-plum hover:bg-white/85 transition-colors"
        title="Fullscreen"
        data-testid="director-fullscreen-btn"
      >
        <Maximize size={14} />
      </button>

      {/* Hover-revealed chrome */}
      <div className="absolute top-3 left-3 z-[90] opacity-0 hover:opacity-100 focus-within:opacity-100 transition-opacity">
        <Link
          to="/welcome"
          className="glass rounded-full px-3 py-1.5 flex items-center gap-2 text-plum text-xs"
          data-testid="director-exit"
        >
          <ArrowLeft size={12} />
          <span className="label-eyebrow" style={TE}>Exit Director</span>
        </Link>
      </div>
      <div className="absolute top-3 right-3 z-[90] opacity-0 hover:opacity-100 focus-within:opacity-100 transition-opacity">
        <Link
          to="/admin"
          className="glass rounded-full px-3 py-1.5 flex items-center gap-2 text-plum text-xs"
          data-testid="director-admin"
        >
          <Settings size={12} />
          <span className="label-eyebrow" style={TE}>Admin</span>
        </Link>
      </div>

      {/* Phase badge — bottom-left, opacity 0.5 default */}
      <div
        className="absolute bottom-3 left-3 z-[90] glass rounded-full px-3 py-1 text-plum text-xs opacity-50 hover:opacity-100 transition-opacity"
        data-testid="director-phase-badge"
      >
        <span className="label-eyebrow" style={TE}>
          {mode} {state.phase !== "auto" && `· ${state.phase}`}
        </span>
      </div>

      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes floatY {
          0%,100% { transform: translateY(0); }
          50%     { transform: translateY(-14px); }
        }
        @keyframes firework {
          0%   { transform: scale(0.4); opacity: 1; }
          70%  { transform: scale(2.5); opacity: 0.8; }
          100% { transform: scale(3.6); opacity: 0; }
        }
        @keyframes arrivalIn {
          from { opacity: 0; transform: translate(-50%, -10px) scale(0.95); }
          to   { opacity: 1; transform: translate(-50%, 0) scale(1); }
        }
        @keyframes confettiFall {
          0%   { transform: translateY(0) rotate(0deg); opacity: 0; }
          10%  { opacity: 0.85; }
          100% { transform: translateY(110vh) rotate(720deg); opacity: 0; }
        }
        @keyframes roamA {
          0%,100% { transform: translate(0,0) scale(1); }
          25%  { transform: translate(7vw,-5vh) scale(1.05); }
          50%  { transform: translate(-5vw,6vh) scale(0.95); }
          75%  { transform: translate(4vw,4vh) scale(1.02); }
        }
        @keyframes roamB {
          0%,100% { transform: translate(0,0); }
          33%  { transform: translate(-6vw,4vh); }
          66%  { transform: translate(5vw,-5vh); }
        }
        @keyframes roamC {
          0%,100% { transform: translate(0,0) rotate(0deg); }
          50%  { transform: translate(8vw,5vh) rotate(4deg); }
        }
        @keyframes twinkle {
          0%   { opacity: 0.45; transform: scale(0.94); }
          100% { opacity: 1;    transform: scale(1.06); }
        }
        @keyframes polaroidFloat {
          0%   { transform: var(--base-rot, rotate(0deg)) translateY(0); }
          50%  { transform: var(--base-rot, rotate(0deg)) translateY(-10px); }
          100% { transform: var(--base-rot, rotate(0deg)) translateY(0); }
        }
      `}</style>
    </div>
  );
}
